import { useEffect, useState } from "react";

import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { Card } from "./ui/card.js";
import { Checkbox } from "./ui/checkbox.js";
import { useConfirm } from "./ui/confirm-dialog.js";

interface Setting { enabled: boolean }

/**
 * Org-wide switch for the attendance approval workflow.
 *
 * Off means employees simply check in and check out: no daily correction requests,
 * no weekly-hours sign-off, no weekly-hours corrections. Anything already pending
 * stays approvable so a queue raised beforehand can still be cleared.
 *
 * Every flip is confirmed first — unlike the other single-checkbox settings, this
 * one changes what every employee in the organisation can do, and a mis-click would
 * be invisible until someone tried to raise a correction.
 *
 * Super HR admin only; the route enforces it too, so hiding the tab is presentation,
 * not access control.
 */
export function AttendanceApprovalSettingCard() {
  const confirm = useConfirm();
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get<Setting>("/admin/attendance-approval-setting")
      .then((r) => setEnabled(r.data.enabled))
      .catch((e) => { logError("AttendanceApprovalSettingCard.load", e); setErr("Could not load this setting."); })
      .finally(() => setLoading(false));
  }, []);

  async function toggle(next: boolean) {
    const ok = await confirm(next
      ? {
          title: "Turn the approval workflow on?",
          description: "Employees will be able to raise attendance and weekly-hours corrections again, managers will go back to approving weekly hours, and the attendance alerts you have enabled start running again.",
          actionText: "Turn on",
          tone: "default",
        }
      : {
          title: "Turn the approval workflow off?",
          description: "For every employee in the organisation, attendance becomes check in and check out only — no new corrections can be raised, weekly hours stop needing sign-off, and every attendance alert stops firing and notifying. Requests already pending stay approvable.",
          actionText: "Turn off",
          tone: "warning",
        });
    // Nothing has changed yet on cancel: the checkbox is driven by `enabled`,
    // which is only moved once the server has accepted the change.
    if (!ok) return;

    setSaving(true);
    setErr("");
    setEnabled(next);
    try {
      const r = await api.patch<Setting>("/admin/attendance-approval-setting", { enabled: next });
      setEnabled(r.data.enabled);
    } catch (e) {
      logError("AttendanceApprovalSettingCard.toggle", e);
      setEnabled(!next);
      setErr("Could not save. The setting is unchanged.");
    } finally { setSaving(false); }
  }

  return (
    <Card className="p-5">
      <label className="flex cursor-pointer items-start gap-3">
        <Checkbox
          checked={enabled}
          disabled={loading || saving}
          onCheckedChange={(v) => void toggle(v === true)}
          className="mt-0.5"
          aria-label="Attendance approval workflow"
        />
        <span>
          <span className="block text-[14px] font-bold">Attendance approval workflow</span>
          <span className="mt-1 block text-[12px] text-surface-500">
            When on, employees can raise daily attendance corrections and weekly-hours
            corrections, managers approve weekly hours, and the attendance alerts below
            run. Turn it off to let employees simply check in and check out — no
            corrections, no sign-off, and no alerts fire or notify. Checking in without
            checking out stays allowed either way, and requests already pending stay
            approvable so nothing is stranded.
          </span>
        </span>
      </label>
      {err && <p className="mt-2 text-[12px] text-[var(--color-error)]">{err}</p>}
    </Card>
  );
}
